import React, { useEffect, useMemo, useState } from "react";
import logoLight from "./assets/logo-light.svg";
import logoDark from "./assets/logo-dark.svg";
import { sanitizeHtml } from "./sanitizeHtml";
import AudioPlayer from "./AudioPlayer";
import {
    API_URL,
    getPublicEpisodeShare,
    listPublicEpisodeShareFiles,
    getPublicEpisodeShareFileStreamUrl,
    buildPublicEpisodeShareFileStreamProxyUrl,
} from "./api";
import "./PortalUI.css";
import "./ReviewPage.css";

function PublicBrand() {
    return (
        <div className="review-brand">
            <picture>
                <source srcSet={logoLight} media="(prefers-color-scheme: dark)" />
                <img src={logoDark} alt="Urban Podcasts" className="review-brand__logo" />
            </picture>
        </div>
    );
}

function getTokenFromUrl() {
    const searchToken = new URLSearchParams(window.location.search).get("token");
    if (searchToken) return searchToken;

    const hash = window.location.hash || "";
    const [, afterHash] = hash.split("#/");
    if (!afterHash) return null;
    const [, qs] = afterHash.split("?");
    if (!qs) return null;
    return new URLSearchParams(qs).get("token");
}

function toLocalYmd(dateObj) {
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, "0");
    const d = String(dateObj.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

function formatPublishDateLabel(value) {
    if (!value) return "";
    const text = String(value).slice(0, 10);
    const dt = new Date(`${text}T00:00:00`);
    if (Number.isNaN(dt.getTime())) return String(value);

    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    if (text === toLocalYmd(today)) return "Today";
    if (text === toLocalYmd(tomorrow)) return "Tomorrow";
    return dt.toLocaleDateString("en-GB");
}

function isDistributionEnabled(value) {
    if (value === null || value === undefined || value === "") return true;
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    const normalized = String(value).trim().toLowerCase();
    if (["0", "false", "no", "off"].includes(normalized)) return false;
    if (["1", "true", "yes", "on"].includes(normalized)) return true;
    return true;
}

function formatEpisodeCodeLabel(seasonNumber, episodeNumber) {
    const episode = Number.parseInt(episodeNumber, 10);
    if (!Number.isFinite(episode) || episode <= 0) return "";

    const season = Number.parseInt(seasonNumber, 10);
    if (Number.isFinite(season) && season > 0) {
        return `S${season}E${episode}`;
    }

    return `E${episode}`;
}

function formatDateTime(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function formatFileSize(bytes) {
    const value = Number(bytes || 0);
    if (!value) return "-";
    const units = ["B", "KB", "MB", "GB", "TB"];
    let size = value;
    let idx = 0;
    while (size >= 1024 && idx < units.length - 1) {
        size /= 1024;
        idx += 1;
    }
    return `${size.toFixed(idx === 0 ? 0 : 1)} ${units[idx]}`;
}

function formatModified(value) {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function getFileExtension(name) {
    const raw = String(name || "").trim();
    const idx = raw.lastIndexOf(".");
    if (idx <= 0 || idx === raw.length - 1) return "";
    return raw.slice(idx + 1).toLowerCase();
}

function isAudioFile(item) {
    const mime = String(item?.mime_type || "").toLowerCase();
    if (mime.startsWith("audio/")) return true;
    const ext = getFileExtension(item?.name);
    return [
        "mp3",
        "m4a",
        "aac",
        "wav",
        "flac",
        "ogg",
        "oga",
        "opus",
        "mpga",
        "aif",
        "aiff",
        "wma",
    ].includes(ext);
}

function isImageFile(item) {
    const mime = String(item?.mime_type || "").toLowerCase();
    if (mime.startsWith("image/")) return true;
    const ext = getFileExtension(item?.name);
    return ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "heic"].includes(ext);
}

function isVideoFile(item) {
    const mime = String(item?.mime_type || "").toLowerCase();
    if (mime.startsWith("video/")) return true;
    const ext = getFileExtension(item?.name);
    return ["mp4"].includes(ext);
}

function normalizeExternalLinkUrl(value) {
    const trimmed = String(value || "").trim();
    if (!trimmed) return "";
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
}

function joinPath(basePath, segment) {
    if (!basePath || basePath === "/") return `/${segment}`;
    return `${basePath}/${segment}`.replace(/\/{2,}/g, "/");
}

function breadcrumbSegments(path) {
    const safe = String(path || "/");
    if (safe === "/") return [{ label: "Home", path: "/" }];

    const parts = safe.split("/").filter(Boolean);
    const out = [{ label: "Home", path: "/" }];
    let current = "";
    for (const part of parts) {
        current += `/${part}`;
        out.push({ label: part, path: current });
    }
    return out;
}

function EpisodeSharePage() {
    const token = getTokenFromUrl();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [data, setData] = useState(null);

    const [path, setPath] = useState("/");
    const [filesLoading, setFilesLoading] = useState(false);
    const [filesError, setFilesError] = useState("");
    const [files, setFiles] = useState([]);

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState("");
    const [previewName, setPreviewName] = useState("");
    const [previewType, setPreviewType] = useState("");
    const [previewUrl, setPreviewUrl] = useState("");
    const [activeFileMenuId, setActiveFileMenuId] = useState(null);
    const [shareAudioUrl, setShareAudioUrl] = useState("");
    const [shareAudioLoading, setShareAudioLoading] = useState(false);

    const breadcrumbs = useMemo(() => breadcrumbSegments(path), [path]);

    useEffect(() => {
        async function load() {
            if (!token) {
                setError("Missing share token in the link.");
                setLoading(false);
                return;
            }
            setLoading(true);
            setError("");
            try {
                const res = await getPublicEpisodeShare(token);
                if (!res?.ok) {
                    setError(res?.message || "Failed to load shared episode");
                    setData(null);
                } else {
                    setData(res);
                }
            } catch {
                setError("Failed to load shared episode");
            } finally {
                setLoading(false);
            }
        }
        load();
    }, [token]);

    useEffect(() => {
        async function loadFiles() {
            if (!token || !data?.episode?.can_view_files) return;
            setFilesLoading(true);
            setFilesError("");
            try {
                const res = await listPublicEpisodeShareFiles(token, path);
                if (!res?.ok) {
                    setFilesError(res?.message || "Failed to load files");
                    setFiles([]);
                    return;
                }
                setFiles(Array.isArray(res.items) ? res.items : []);
                setPath(res.path || "/");
            } catch {
                setFilesError("Failed to load files");
                setFiles([]);
            } finally {
                setFilesLoading(false);
            }
        }
        loadFiles();
    }, [token, path, data?.episode?.can_view_files]);

    useEffect(() => {
        async function loadShareAudio() {
            setShareAudioUrl("");

            const episode = data?.episode;
            if (!token || !episode) return;

            if (episode.episode_mp3 && /^https?:\/\//i.test(String(episode.episode_mp3))) {
                return;
            }

            const itemId = String(episode.selected_audio_file_item_id || "").trim();
            if (!itemId) return;

            setShareAudioLoading(true);
            try {
                const urlRes = await getPublicEpisodeShareFileStreamUrl(token, itemId);
                if (urlRes?.ok && urlRes.stream_url) {
                    setShareAudioUrl(String(urlRes.stream_url));
                }
            } finally {
                setShareAudioLoading(false);
            }
        }
        loadShareAudio();
    }, [token, data?.episode]);

    useEffect(() => {
        if (!previewOpen) return undefined;
        const onKeyDown = (e) => {
            if (e.key !== "Escape") return;
            e.preventDefault();
            e.stopPropagation();
            setPreviewOpen(false);
            setPreviewLoading(false);
            setPreviewError("");
            setPreviewName("");
            setPreviewType("");
            setPreviewUrl("");
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [previewOpen]);

    useEffect(() => {
        const handleDocumentClick = () => {
            setActiveFileMenuId(null);
        };
        document.addEventListener("click", handleDocumentClick);
        return () => document.removeEventListener("click", handleDocumentClick);
    }, []);

    const openFolder = (item) => {
        if (!item || item.type !== "folder") return;
        setPath((prev) => joinPath(prev, item.name));
    };

    const goToPath = (nextPath) => {
        setPath(nextPath || "/");
    };

    const goUp = () => {
        if (path === "/") return;
        const parent = path.split("/").filter(Boolean).slice(0, -1);
        setPath(parent.length ? `/${parent.join("/")}` : "/");
    };

    const closePreview = () => {
        setPreviewOpen(false);
        setPreviewLoading(false);
        setPreviewError("");
        setPreviewName("");
        setPreviewType("");
        setPreviewUrl("");
    };

    const handlePreview = async (item) => {
        const type = isAudioFile(item)
            ? "audio"
            : isVideoFile(item)
              ? "video"
            : isImageFile(item)
              ? "image"
            : "";
        if (!item?.id || item.type !== "file" || !type) return;

        setPreviewOpen(true);
        setPreviewLoading(true);
        setPreviewError("");
        setPreviewName(item.name || "Preview");
        setPreviewType(type);
        setPreviewUrl("");

        if (type === "video") {
            const urlRes = await getPublicEpisodeShareFileStreamUrl(token, item.id);
            if (!urlRes || !urlRes.ok) {
                setPreviewLoading(false);
                setPreviewError(urlRes?.message || "Failed to load preview");
                return;
            }
            setPreviewUrl(buildPublicEpisodeShareFileStreamProxyUrl(token, item.id));
            setPreviewLoading(false);
            return;
        }

        if (item.download_url) {
            setPreviewUrl(String(item.download_url));
            setPreviewLoading(false);
            return;
        }

        const urlRes = await getPublicEpisodeShareFileStreamUrl(token, item.id);
        if (!urlRes || !urlRes.ok || !urlRes.stream_url) {
            setPreviewLoading(false);
            setPreviewError(urlRes?.message || "Failed to load preview");
            return;
        }
        setPreviewUrl(String(urlRes.stream_url));
        setPreviewLoading(false);
    };

    const handleDownload = async (item) => {
        if (!item?.id || item.type !== "file") return;

        const useUrl = item.download_url
            ? String(item.download_url)
            : String((await getPublicEpisodeShareFileStreamUrl(token, item.id))?.stream_url || "");

        if (!useUrl) return;

        const anchor = document.createElement("a");
        anchor.href = useUrl;
        anchor.download = item.name || "download.bin";
        anchor.rel = "noopener noreferrer";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
    };

    const renderFileRowActions = (item) => {
        if (!item || item.type !== "file") return null;
        const isOpen = activeFileMenuId === item.id;
        const canPreview = isAudioFile(item) || isVideoFile(item) || isImageFile(item);

        return (
            <>
                <div
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                    }}
                >
                    <span
                        aria-hidden="true"
                        style={{
                            width: 1,
                            height: 18,
                            background: "rgba(148, 163, 184, 0.35)",
                            display: "inline-block",
                        }}
                    />
                    <button
                        type="button"
                        style={{
                            border: "none",
                            background: "transparent",
                            color: "rgba(148, 163, 184, 0.9)",
                            cursor: "pointer",
                            fontSize: 14,
                            fontWeight: 700,
                            lineHeight: 1,
                            width: 16,
                            height: 16,
                            borderRadius: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                            opacity: 0.85,
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setActiveFileMenuId((current) => (current === item.id ? null : item.id));
                        }}
                        aria-label="Open actions menu"
                    >
                        ⋯
                    </button>
                </div>
                {isOpen ? (
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: "absolute",
                            right: 0,
                            bottom: "calc(100% + 4px)",
                            zIndex: 20,
                            minWidth: 150,
                            maxHeight: 240,
                            overflowY: "auto",
                            border: "1px solid #334155",
                            borderRadius: 8,
                            background: "#0f172a",
                            padding: 6,
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                        }}
                    >
                        {canPreview ? (
                            <button
                                type="button"
                                className="btn btn--secondary btn--tiny"
                                onClick={() => {
                                    setActiveFileMenuId(null);
                                    handlePreview(item);
                                }}
                            >
                                Preview
                            </button>
                        ) : null}
                        <button
                            type="button"
                            className="btn btn--secondary btn--tiny"
                            onClick={() => {
                                setActiveFileMenuId(null);
                                handleDownload(item);
                            }}
                        >
                            Download
                        </button>
                    </div>
                ) : null}
            </>
        );
    };

    const episode = data?.episode || {};
    const share = data?.share || {};
    const episodeCode = formatEpisodeCodeLabel(episode.season_number, episode.episode_number);
    const showNotesHtml = String(episode.show_notes_captivate || "").trim();
    const externalLinks = Array.isArray(data?.external_links) ? data.external_links : [];
    const audioUrl = (() => {
        if (episode.episode_mp3 && /^https?:\/\//i.test(String(episode.episode_mp3))) {
            return String(episode.episode_mp3);
        }
        return shareAudioUrl || "";
    })();
    const handleDownloadReviewAudio = async () => {
        if (!token) return;
        const safeBase =
            String(episode?.title || "episode-audio")
                .trim()
                .replace(/[^\w\- ]+/g, "")
                .replace(/\s+/g, "-")
                .toLowerCase() || "episode-audio";

        const filename = `${safeBase}.mp3`;
        const downloadUrl = `${API_URL}/episodes/share/public/${encodeURIComponent(
            token
        )}/audio/download`;

        const anchor = document.createElement("a");
        anchor.href = downloadUrl;
        anchor.download = filename;
        anchor.rel = "noopener noreferrer";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
    };

    if (loading) {
        return (
            <div className="review-page">
                <PublicBrand />
                <div className="review-card card review-card--narrow">
                    <p>Loading shared episode...</p>
                </div>
            </div>
        );
    }

    if (error || !data?.ok) {
        return (
            <div className="review-page">
                <PublicBrand />
                <div className="review-card card review-card--narrow">
                    <h1 className="review-title">Episode Share</h1>
                    <div className="alert alert--error">{error || "Invalid share link"}</div>
                    <div style={{ marginTop: 12 }}>
                        <a
                            href="#/login"
                            className="btn btn--secondary"
                            style={{ width: "100%", justifyContent: "center" }}
                        >
                            Sign in to Urban Podcasts Portal
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="review-page">
            <PublicBrand />
            <div className="review-card card">
                <header className="review-header">
                    <div className="review-badge">Shared episode</div>
                    <h1 className="review-title">{episode.title || "Episode"}</h1>
                    <p className="review-podcast">
                        {episode.podcast_name || "Podcast"}
                        {episodeCode ? ` • ${episodeCode}` : ""}
                    </p>
                </header>

                <section className="review-section">
                    <h2 className="review-section-title">Publication details</h2>
                    <p className="review-meta">
                        {isDistributionEnabled(episode?.podcast_distribution) &&
                        formatPublishDateLabel(episode.publication_date) ? (
                            <span>
                                <strong>Date:</strong>{" "}
                                {formatPublishDateLabel(episode.publication_date)}
                            </span>
                        ) : null}
                        {isDistributionEnabled(episode?.podcast_distribution) &&
                        String(episode.publication_time || "").slice(0, 5) ? (
                            <span>
                                <strong>Time:</strong>{" "}
                                {String(episode.publication_time || "").slice(0, 5)}
                            </span>
                        ) : null}
                        <span>
                            <strong>Status:</strong> {episode.status || "-"}
                        </span>
                        <span>
                            <strong>Share Expires:</strong> {formatDateTime(share.expires_at) || "-"}
                        </span>
                    </p>
                </section>

                {String(episode.notes || "").trim() ? (
                    <section className="review-section">
                        <h2 className="review-section-title">Episode notes</h2>
                        <p className="review-send-notes">{String(episode.notes || "")}</p>
                    </section>
                ) : null}

                <section className="review-section review-media">
                    <div className="review-audio">
                        <h3 className="review-section-title">Episode Audio</h3>
                        {shareAudioLoading ? <p className="text-description">Loading audio...</p> : null}
                        {!shareAudioLoading && audioUrl ? (
                            <AudioPlayer
                                src={audioUrl}
                                onDownload={handleDownloadReviewAudio}
                                audioClassName="review-audio-player"
                            />
                        ) : null}
                        {!shareAudioLoading && !audioUrl ? (
                            <p className="text-description">No audio available.</p>
                        ) : null}
                    </div>
                </section>

                {showNotesHtml ? (
                    <section className="review-section">
                        <h2 className="review-section-title">Show notes</h2>
                        <div
                            className="review-show-notes"
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(showNotesHtml) }}
                        />
                    </section>
                ) : null}

                {externalLinks.length > 0 ? (
                    <section className="review-section">
                        <h2 className="review-section-title">External links</h2>
                        <table className="change-log-table file-explorer__table">
                            <colgroup>
                                <col style={{ width: "30%" }} />
                                <col style={{ width: "70%" }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>Title</th>
                                    <th>URL</th>
                                </tr>
                            </thead>
                            <tbody>
                                {externalLinks.map((link) => {
                                    const safeExternalUrl = normalizeExternalLinkUrl(link.url);
                                    return (
                                        <tr key={link.id}>
                                            <td>{link.title || "-"}</td>
                                            <td>
                                                {safeExternalUrl ? (
                                                    <a
                                                        href={safeExternalUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="file-explorer__name-link"
                                                    >
                                                        {link.url}
                                                    </a>
                                                ) : (
                                                    <span>{link.url || "-"}</span>
                                                )}
                                                {link.notes ? (
                                                    <div
                                                        className="text-description"
                                                        style={{ marginTop: 4, fontSize: 12 }}
                                                    >
                                                        {link.notes}
                                                    </div>
                                                ) : null}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </section>
                ) : null}

                {episode.can_view_files ? (
                    <section className="review-section">
                        <h2 className="review-section-title">Episode files</h2>

                        <div className="file-explorer__path-bar">
                            <button
                                type="button"
                                className="btn btn--secondary btn--small"
                                onClick={goUp}
                                disabled={path === "/" || filesLoading}
                            >
                                Up
                            </button>

                            <div className="file-explorer__breadcrumbs">
                                {breadcrumbs.map((crumb, index) => (
                                    <button
                                        key={crumb.path}
                                        type="button"
                                        className={
                                            "file-explorer__crumb" +
                                            (index === breadcrumbs.length - 1
                                                ? " file-explorer__crumb--active"
                                                : "")
                                        }
                                        onClick={() => goToPath(crumb.path)}
                                        disabled={filesLoading || index === breadcrumbs.length - 1}
                                    >
                                        {crumb.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {filesError ? <div className="alert alert--error">{filesError}</div> : null}
                        {filesLoading ? (
                            <p className="text-description" style={{ marginTop: 8 }}>
                                Loading files...
                            </p>
                        ) : null}

                        {!filesLoading && files.length === 0 ? (
                            <p className="text-description" style={{ marginTop: 8 }}>
                                No files in this folder.
                            </p>
                        ) : null}

                        {!filesLoading && files.length > 0 ? (
                            <table className="change-log-table file-explorer__table" style={{ marginTop: 8 }}>
                                <colgroup>
                                    <col style={{ width: "60%" }} />
                                    <col style={{ width: "10%" }} />
                                    <col style={{ width: "20%" }} />
                                    <col style={{ width: "10%" }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th className="file-explorer__col-mobile-hide">Size</th>
                                        <th className="file-explorer__col-mobile-hide">Modified</th>
                                        <th>&nbsp;</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {files.map((item) => {
                                        const isSelectedEpisodeAudio =
                                            String(episode.selected_audio_file_item_id || "") ===
                                            String(item.id || "");
                                        const isSelectedEpisodeArtwork =
                                            String(episode.selected_artwork_file_item_id || "") ===
                                            String(item.id || "");
                                        return (
                                        <tr key={item.id}>
                                            <td>
                                                <div className="file-explorer__name-cell">
                                                    <span className="file-explorer__name-icon">
                                                        {item.type === "folder" ? "📁" : "📄"}
                                                    </span>
                                                    {item.type === "folder" ? (
                                                        <button
                                                            type="button"
                                                            className="file-explorer__name-link"
                                                            onClick={() => openFolder(item)}
                                                        >
                                                            {item.name}
                                                        </button>
                                                    ) : (
                                                        <span>{item.name}</span>
                                                    )}
                                                    {(isSelectedEpisodeAudio ||
                                                        isSelectedEpisodeArtwork) && (
                                                        <span className="file-explorer__usage-badges">
                                                            {isSelectedEpisodeAudio && (
                                                                <span className="file-explorer__usage-badge file-explorer__usage-badge--audio">
                                                                    Episode Audio
                                                                </span>
                                                            )}
                                                            {isSelectedEpisodeArtwork && (
                                                                <span className="file-explorer__usage-badge file-explorer__usage-badge--artwork">
                                                                    Episode Artwork
                                                                </span>
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="file-explorer__col-mobile-hide">
                                                {item.type === "file" ? formatFileSize(item.size) : "-"}
                                            </td>
                                            <td className="file-explorer__col-mobile-hide">
                                                {formatModified(item.last_modified)}
                                            </td>
                                            <td>
                                                <div
                                                    className="file-explorer__actions"
                                                    style={{ justifyContent: "flex-end", position: "relative" }}
                                                >
                                                    {renderFileRowActions(item)}
                                                </div>
                                            </td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        ) : null}
                    </section>
                ) : null}
            </div>

            {previewOpen ? (
                <div className="modal-overlay" onClick={closePreview}>
                    <div className="modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">Preview</h3>
                                <p className="modal__subtitle">{previewName || "File preview"}</p>
                            </div>
                            <button className="modal__close-btn" onClick={closePreview}>
                                ✕
                            </button>
                        </div>
                        <div className="modal__body">
                            {previewLoading ? <p>Loading preview...</p> : null}
                            {!previewLoading && previewError ? (
                                <div className="alert alert--error">{previewError}</div>
                            ) : null}
                            {!previewLoading && !previewError && previewUrl && previewType === "audio" ? (
                                <AudioPlayer src={previewUrl} />
                            ) : null}
                            {!previewLoading && !previewError && previewUrl && previewType === "video" ? (
                                <video
                                    src={previewUrl}
                                    controls
                                    preload="metadata"
                                    style={{
                                        width: "100%",
                                        maxHeight: "70vh",
                                        borderRadius: 8,
                                        background: "#000",
                                    }}
                                />
                            ) : null}
                            {!previewLoading && !previewError && previewUrl && previewType === "image" ? (
                                <img
                                    src={previewUrl}
                                    alt={previewName || "Image preview"}
                                    style={{
                                        width: "100%",
                                        maxHeight: "70vh",
                                        objectFit: "contain",
                                        borderRadius: 8,
                                    }}
                                />
                            ) : null}
                        </div>
                        <div className="modal__footer">
                            <span />
                            <button type="button" className="btn btn--secondary" onClick={closePreview}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export default EpisodeSharePage;
